// The provider contract (D-022, spec: HQ 02_ENGINEERING/Data_Providers.md).
// Everything above this layer asks for candles/closes in the app's existing
// range/interval vocabulary ("3mo"/"1d", "5d"/"30m", …) and never learns
// where the data came from. Adapters run client-side: a user's API key
// lives in their browser and talks only to their own provider.

import type { OHLCPoint } from "../market-data.js";
import type { AssetClass } from "../stock-directory.js";

export interface CandleResult {
  candles: OHLCPoint[];
  /** Latest traded price when the source reports one (else derive from last close). */
  currentPrice?: number;
}

export interface ClosesResult {
  closes: number[];
  regularMarketPrice?: number;
}

export interface ProviderSetupInfo {
  signupUrl: string;
  keyLabel: string;
  costNote: string;
}

export interface ByoField {
  key: string;
  label: string;
  type: "text" | "password";
}

/** Bring-your-own credentials contract — drives the Settings UI
 * generically, so adding provider #4…#20 never touches the page. */
export interface ByoConfig {
  fields: ByoField[];
  getCreds(): Promise<Record<string, string> | null>;
  setCreds(creds: Record<string, string> | null): Promise<void>;
  verify(creds: Record<string, string>): Promise<{ ok: boolean; message?: string }>;
}

export interface DataProvider {
  id: string;
  displayName: string;
  /** Which asset classes this provider can serve. */
  assetClasses: AssetClass[];
  realtime: boolean;
  setup: ProviderSetupInfo | null; // null = no credentials needed (free)
  byo?: ByoConfig;
  candles(symbol: string, range: string, interval: string): Promise<CandleResult>;
  closes(symbol: string, range: string, interval: string): Promise<ClosesResult>;
}

/** Shared range/interval helpers — pure, unit-tested. */

export function rangeToMs(range: string): number {
  const match = /^(\d+)(m|h|d|wk|mo|y)$/.exec(range.trim());
  if (!match) return 30 * 24 * 3600_000;
  const n = Number(match[1]);
  const unitMs: Record<string, number> = {
    m: 60_000,
    h: 3600_000,
    d: 24 * 3600_000,
    wk: 7 * 24 * 3600_000,
    mo: 30 * 24 * 3600_000,
    y: 365 * 24 * 3600_000,
  };
  return n * (unitMs[match[2]!] ?? 24 * 3600_000);
}

export function intervalToMs(interval: string): number {
  const match = /^(\d+)(m|h|d|wk|mo)$/.exec(interval.trim());
  if (!match) return 24 * 3600_000;
  const n = Number(match[1]);
  const unitMs: Record<string, number> = {
    m: 60_000,
    h: 3600_000,
    d: 24 * 3600_000,
    wk: 7 * 24 * 3600_000,
    mo: 30 * 24 * 3600_000,
  };
  return n * (unitMs[match[2]!] ?? 24 * 3600_000);
}
